import React, { useEffect, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";

import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  flexRender,
} from "@tanstack/react-table";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  ModalOverlay,
} from "@chakra-ui/modal";
import UserDetails from "./user-details";
import DarkButton from "components/actions/DarkButton";
import PenEditIcon from "components/icons/PenEditIcon";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import NewUser from "./new-user";
import EditUser from "./edit-user";
import Select from "components/fields/Select";
import { useDarkmode } from "hooks/useTheme";
import StatusLabel from "components/label/StatusLabel";
import AtriumApi from "api";
import Avatar from "components/avatar";
import ChevronLeft from "components/icons/ChevronLeft";
import ChevronRight from "components/icons/ChevronRight";
import { FaUsersSlash } from "react-icons/fa";
import Loader from "components/loader/Loader";
import { PiUsersThreeLight } from "react-icons/pi";
import { PiUsersLight } from "react-icons/pi";
import { FaPeopleRoof } from "react-icons/fa6";
import { LuHandshake } from "react-icons/lu";
import { SiInternetcomputer } from "react-icons/si";
import toast from "react-hot-toast";
import UploadCsvDialog from "./upload-csv-dialog";
import Papa from "papaparse";
import { saveAs } from "file-saver";

const columnHelper = createColumnHelper();

const People = () => {
  // Static config layout (no count here)
  const statLayout = [
    {
      key: "totalUsers",
      title: "Total Users",
      icon: <PiUsersThreeLight className="text-[#22C55E] text-2xl" />,
      bgColor: "bg-[#C6F1E8]",
    },
    {
      key: "active",
      title: "Active",
      icon: <PiUsersLight className="text-[#54996d] text-2xl" />, // Tailwind's green-700
      bgColor: "bg-[#95e9b3]", // deeper than #D1FAEA
    },

    {
      key: "inactive",
      title: "Inactive",
      icon: <FaUsersSlash className="text-[#F53939] text-2xl" />,
      bgColor: "bg-[#f38585]",
    },
    {
      key: "members",
      title: "Members",
      icon: <FaPeopleRoof className="text-[#2563EB] text-2xl" />, // Tailwind's blue-600
      bgColor: "bg-[#8baffc]", // Tailwind's blue-100
    },
    {
      key: "volunteers",
      title: "Volunteers",
      icon: <LuHandshake className="text-[#D97706] text-2xl" />, // Tailwind's amber-600
      bgColor: "bg-[#ffca8d]", // Tailwind's amber-100
    },
    {
      key: "fromPlanningCenter",
      title: "Planning Center",
      icon: <SiInternetcomputer className="text-[#4f30fd] text-2xl" />, // Tailwind's indigo-600
      bgColor: "bg-[#8f7bfd]", // Tailwind's indigo-100
    },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!params.id,
    onClose: () => {
      navigate("/admin/people");
    },
  });

  const [openCsvDialog, setOpenCsvDialog] = useState(false);

  const [{ rowCount, page, per }, setPagination] = useState({
    rowCount: 0,
    page: 1,
    per: 10,
  });

  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [queryOptions, setQueryOptions] = useState({ page: 1, per: 10 });

  const [user, setUser] = useState(undefined);

  const [statsSummary, setStatsSummary] = useState(undefined);

  const [readonlyForm, setReadonlyForm] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const { darkmode } = useDarkmode();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    setReadonlyForm(
      matchPath("/admin/people/:id/edit", location.pathname) === null
    );
  }, [location]);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Update queryOptions when debounced input changes
  useEffect(() => {
    setQueryOptions((prev) => ({
      ...prev,
      q: debouncedSearchInput,
      page: 1,
    }));
  }, [debouncedSearchInput]);

  // API call on queryOptions change

  const getUsers = () => {
    setLoading(true);
    AtriumApi.get("/v1/users", { params: queryOptions })
      .then((response) => {
        setUsers(response.data.users);
        setPagination(response.data.pagination);
        setStatsSummary(response.data.summary);
        setLoading(false);
      })
      .catch((error) => {
        setUsers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    getUsers();
  }, [location, queryOptions]);

  const prevPage = (e) => {
    e.preventDefault();
    if (page <= 1) return;
    setQueryOptions({ ...queryOptions, page: page - 1, per: per });
    // setPagination({rowCount: rowCount, page: page - 1, per: per})
  };

  const nextPage = (e) => {
    e.preventDefault();
    if (rowCount <= page * per) return;
    setQueryOptions({ ...queryOptions, page: page + 1, per: per });
    // setPagination({rowCount: rowCount, page: page + 1, per: per})
  };

  const statsData =
    statsSummary &&
    statLayout.map((item) => ({
      ...item,
      count: statsSummary[item.key] ?? 0, // fallback to 0 if key not found
    }));

  const addUser = () => {
    setUser(undefined);
    onOpen();
    navigate("/admin/people/new");
  };

  const editProfile = (user) => {
    setUser(user);
    onOpen();
    navigate("/admin/people/" + user.id + "/edit");
  };

  const showProfile = (user) => {
    setUser(user);
    onOpen();
    navigate("/admin/people/" + user.id);
  };

  // useEffect(() => {
  //   const getUser = async () => {
  //     try {
  //       const response = await AtriumApi.get("/v1/users/get-all-users");
  //       console.log("csv download resp", response);
  //     } catch (error) {
  //       console.log(error);
  //     }
  //   };
  //   getUser();
  // }, []);

  // download the user in csv file

  const handleExportToCSV = async () => {
    try {
      setExportLoading(true);
      const response = await AtriumApi.get("/v1/users/get-all-users"); // all users, not paginated
      const users = response?.data?.data || [];

      if (!users?.length) {
        toast.error("No user data to export");
        setExportLoading(false);
        return;
      }

      const cleanedUsers = users?.map((user) => ({
        campus_name: user?.campus_name || "",
        household_id: user?.household_id || "",
        household_avatar: user?.household_avatar || "",
        household_member_count: user?.household_member_count || "",
        household_name: user?.household_name,
        household_primary_contact_id: user?.household_primary_contact_id || "",
        household_primary_contact_name:
          user?.household_primary_contact_name || "",
        user_email: user?.user_email || "",
        user_phone: user?.user_phone || "",
        user_gender: user?.user_gender || "",
        user_date_of_birth: user?.user_date_of_birth || "",
        user_first_name: user?.user_first_name || "",
        user_last_name: user?.user_last_name || "",
        user_family_relationship: user?.user_family_relationship || "",
        user_address: user?.user_address || "",
        user_city: user?.user_city || "",
        user_state: user?.user_state || "",
        user_country: user?.user_country || "",
        user_zip_code: user?.user_zip_code || "",
        user_avatar: user?.user_avatar || "",
        // user_location: user?.user_location || "",
        user_location: "",
        user_street: user?.user_street || "",
        user_people_id: user?.user_people_id || "",
        user_is_primary: user?.user_is_primary || "",
        user_relationship_text: user?.user_relationship_text || "",
        user_allergies: user?.user_allergies || "",
        user_school_grade: user?.user_school_grade || "",
      }));
      const csv = Papa.unparse(cleanedUsers);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "all-users.csv");
      setExportLoading(false);
      toast.success("file downloaded successfully");
    } catch (error) {
      setExportLoading(false);
      console.error("Export error", error);
      toast.error("Failed to export users");
    }
  };

  const columns = [
    // columnHelper.accessor("selector", {
    //   id: "selector",
    //   header: () => <Checkbox />,
    //   cell: (info) => <Checkbox />,
    //   size: 20,
    //   enableResizing: true,
    //   enableSorting: false,
    // }),
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="flex-1 text-sm font-bold whitespace-nowrap text-gray-600 dark:text-white">
          NAME
        </p>
      ),
      cell: (info) => (
        <div className="flex gap-[15px] align-center justify-center">
          <Avatar user={info.row.original} size="sm" />
          <div className="flex-1 content-center font-medium whitespace-nowrap text-gray-800 dark:text-white">
            {info.row.original.firstName} {info.row.original.lastName}
          </div>
        </div>
      ),
      size: "auto",
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">EMAIL</p>
      ),
      cell: (info) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
      size: "auto",
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ROLE</p>
      ),
      cell: (info) => <StatusLabel status={info.getValue()} />,
      size: 150,
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ID</p>
      ),
      cell: (info) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
      size: "auto",
    }),
    columnHelper.accessor("planningCenter", {
      id: "planningCenter",
      header: () => (
        <p className="flex-1 text-center text-sm font-bold text-gray-600 whitespace-nowrap dark:text-white">
          Planning Center
        </p>
      ),
      cell: (info) => (
        <p
          className={`text-sm text-center capitalize font-bold dark:text-white ${
            info.row.original.fromPlanningCenter
              ? "text-green-600 dark:text-green-600"
              : "text-red-600 dark:text-red-600"
          }`}
        >
          {info.row.original.fromPlanningCenter ? "Yes" : "No"}
        </p>
      ),
      size: "auto",
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          STATUS
        </p>
      ),
      cell: (info) => (
        <div
          className={`flex h-7 inline-block w-fit pl-5 pr-5 items-center justify-center text-sm capitalize rounded-[10px] text-base font-bold
  ${
    info.getValue() === "active"
      ? "bg-green-100 text-green-700 dark:bg-green-200 dark:text-green-800"
      : "bg-red-100 text-red-700 dark:bg-red-200 dark:text-red-800"
  }
`}
        >
          {info.getValue()}
        </div>
      ),
      size: 120,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          ACTIONS
        </p>
      ),
      cell: (info) => (
        <div className="flex gap-5">
          <span
            className="cursor-pointer p-2 text-gray-700"
            onClick={() => editProfile(info.row.original)}
          >
            <PenEditIcon />
          </span>

          <span
            className="cursor-pointer content-center text-gray-700 w-[44px] h-[44px]"
            onClick={() => showProfile(info.row.original)}
          >
            <svg
              className="m-auto"
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.5 11L6.5 6L1.5 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      ),
      size: 120,
    }),
  ]; // eslint-disable-next-line

  const table = useReactTable({
    data: users,
    columns,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
    enableRowSelection: true,
    manualSorting: true,
    manualPagination: true,
    rowCount: rowCount,
    // onSortingChange: (sortingFn) => {}
  });

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose}>
        <ModalOverlay
          bg={darkmode ? "#A3AED080" : "#2B367480"}
          backdropFilter="blur(3px)"
          zIndex={15}
          onClick={() => onClose()}
        />
        <DrawerContent
          containerProps={{ zIndex: 20 }}
          className="my-2 ml-0 mr-4 w-[calc(100vw_-_32px)] max-w-[calc(100vw_-_32px)] rounded-2xl bg-white shadow-[-20px_17px_40px_4px_rgba(112,_144,_176,_0.18)] dark:bg-background-900 dark:shadow-[-22px_32px_51px_4px_#0B1437] sm:ml-4 md:w-[600px] md:max-w-[600px]"
        >
          <DrawerCloseButton className="absolute right-4 top-4 text-xs p-3 text-gray-900 dark:text-white" />

          {params.id === "new" ? (
            <NewUser setUser={setUser} onClose={onClose} />
          ) : readonlyForm ? (
            <UserDetails
              user={user}
              setUser={setUser}
              userId={params.id}
              onClose={onClose}
              setReadonly={setReadonlyForm}
            />
          ) : (
            <EditUser user={user} setUser={setUser} onClose={onClose} />
          )}
        </DrawerContent>
      </Drawer>

      <div className="">
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statsData?.map((stat, index) => (
            <div
              key={index}
              className="flex items-center bg-white dark:bg-navy-900 shadow-md rounded-2xl p-4 w-full  space-x-4"
            >
              <div className={`p-2 rounded-full ${stat.bgColor} bg-opacity-20`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.title}</p>
                <p className="text-2xl font-semibold text-blue-800  dark:text-white">
                  {stat.count.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center flex-col lg:flex-row flex-wrap xl:flex-nowrap gap-3 lg:gap-[30px] mt-8">
          <div className="w-full w-[200px] lg:w-[320px] h-[42px]">
            <input
              type="text"
              placeholder="Find by user name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="block p-4 h-full w-full rounded-2xl bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark::placeholder:!text-gray-700"
            />
          </div>

          <div className="flex flex-1 gap-3 flex-row">
            <Select
              value={queryOptions.role || ""}
              onChange={(e) =>
                setQueryOptions({
                  ...queryOptions,
                  role: e.target.value,
                  page: 1,
                })
              }
              required
            >
              <option value="">All roles</option>
              <option value="member">Member</option>
              <option value="volunteer">Volunteer</option>
            </Select>

            <Select
              value={queryOptions.status || ""}
              onChange={(e) =>
                setQueryOptions({
                  ...queryOptions,
                  status: e.target.value,
                  page: 1,
                })
              }
              required
            >
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>

            {/* <Select
              value={queryOptions.group || ""}
              onChange={(e) =>
                setQueryOptions({
                  ...queryOptions,
                  group: e.target.value,
                  page: 1,
                })
              }
              required
            >
              <option value="">All groups</option>
            </Select> */}
          </div>

          <div className="flex-1 flex md:justify-end">
            <DarkButton
              customClass="h-9 md:h-12"
              onClick={() => setOpenCsvDialog(true)}
            >
              <span className="align-middle">Import from CSV</span>
            </DarkButton>
            <DarkButton customClass="h-9 md:h-12 ml-2" onClick={addUser}>
              <span className="">+</span>{" "}
              <span className="align-middle">Add user</span>
            </DarkButton>
            <DarkButton
              disabled={exportLoading}
              customClass="h-9 md:h-12 ml-2"
              onClick={handleExportToCSV}
            >
              {/* <span className="">+</span>{" "} */}
              <span className="align-middle">
                {exportLoading ? "Exporting..." : "Export to CSV"}
              </span>
            </DarkButton>
          </div>
        </div>
      </div>
      <div>
        <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="!border-px !border-gray-400"
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer border-b border-gray-200 pb-2 pt-4 pr-4 text-start dark:border-white/30"
                        style={{ width: header.getSize() || "auto" }}
                      >
                        <div className="items-center justify-between text-xs text-gray-200">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: "",
                            desc: "",
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={table.getAllColumns().length}>
                    <div className="flex justify-center items-center w-full py-4">
                      <Loader />
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-200 dark:border-white/30"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="border-white/0 py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="text-center py-4 text-gray-500"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && rowCount > 0 && (
          <div className="flex items-center gap-5 my-5 text-sm">
            <div>Show rows per page</div>
            <div>
              <Select
                value={per}
                height="auto"
                onChange={(e) => {
                  setPagination({
                    page: 1,
                    rowCount: rowCount,
                    per: parseInt(e.target.value),
                  });
                  setQueryOptions({
                    ...queryOptions,
                    page: 1,
                    per: parseInt(e.target.value),
                  });
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
              </Select>
            </div>
            <div className="flex-1 text-right">
              <>
                <span>
                  {1 + (page - 1) * per} -{" "}
                  {page * per > rowCount ? rowCount : page * per}
                </span>
                &nbsp;
                <span className="text-gray-600">of {rowCount}</span>
              </>
            </div>
            <div>
              <button
                onClick={prevPage}
                className={`h-full mr-1 p-2 ${
                  page <= 1 ? "text-gray-600" : ""
                }`}
              >
                <ChevronLeft />
              </button>
              <button
                onClick={nextPage}
                className={`h-full mr-1 p-2 ${
                  page * per >= rowCount ? "text-gray-600" : ""
                }`}
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {openCsvDialog && (
        <UploadCsvDialog
          openCsvDialog={openCsvDialog}
          setOpenCsvDialog={setOpenCsvDialog}
          getUsers={getUsers}
        />
      )}
    </>
  );
};

export default People;
